/**
 * JobsTab — Admin management of job postings.
 * Create, edit, toggle active status, delete, and send email blasts to matched users.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { apiService } from '../../services/api';
import { getAdminToken } from './adminUtils';

const LOCATION_TYPES = [
  { value: 'india', label: '🇮🇳 India (Onsite)' },
  { value: 'remote', label: '🌐 Remote' },
  { value: 'international', label: '✈️ International' },
];

const EMPTY_FORM = {
  title: '',
  company: '',
  description: '',
  location_type: 'india',
  city: '',
  required_certificate: '',
  salary_range: '',
  apply_url: '',
};

export const JobsTab = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [blastingId, setBlastingId] = useState(null);

  const adminToken = getAdminToken();

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiService.adminGetJobs(adminToken);
      setJobs(data);
    } catch (err) {
      toast.error('Failed to load jobs');
    } finally {
      setLoading(false);
    }
  }, [adminToken]);

  useEffect(() => { fetchJobs(); }, [fetchJobs]);

  const handleEdit = (job) => {
    setEditingId(job.id);
    setForm({
      title: job.title || '',
      company: job.company || '',
      description: job.description || '',
      location_type: job.location_type || 'india',
      city: job.city || '',
      required_certificate: job.required_certificate || '',
      salary_range: job.salary_range || '',
      apply_url: job.apply_url || '',
    });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleNew = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.company || !form.description) { toast.error('Title, company, and description are required'); return; }
    setSaving(true);
    try {
      if (editingId) {
        await apiService.adminUpdateJob(editingId, form, adminToken);
        toast.success('Job updated');
      } else {
        await apiService.adminCreateJob(form, adminToken);
        toast.success('Job posting created');
      }
      handleCancel();
      fetchJobs();
    } catch (err) {
      toast.error(err?.detail || err?.message || 'Failed to save job');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (job) => {
    try {
      const res = await apiService.adminToggleJob(job.id, adminToken);
      setJobs((prev) => prev.map((j) => j.id === job.id ? { ...j, is_active: res.is_active } : j));
      toast.success(res.message);
    } catch (err) {
      toast.error(err?.detail || 'Failed to toggle job');
    }
  };

  const handleDelete = async (job) => {
    if (!window.confirm(`Delete "${job.title}"? This cannot be undone.`)) return;
    try {
      await apiService.adminDeleteJob(job.id, adminToken);
      toast.success('Job deleted');
      fetchJobs();
    } catch (err) {
      toast.error(err?.detail || 'Failed to delete job');
    }
  };

  const handleBlast = async (job) => {
    if (!window.confirm(`Send job notification emails for "${job.title}"? Only opted-in users matching the criteria will be emailed.`)) return;
    setBlastingId(job.id);
    try {
      const res = await apiService.adminBlastJob(job.id, adminToken);
      toast.success(res.message || 'Email blast started');
    } catch (err) {
      toast.error(err?.detail || 'Failed to start email blast');
    } finally {
      setBlastingId(null);
    }
  };

  const inp = 'w-full bg-slate-900/60 border border-slate-600 rounded-lg px-3 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500/50';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-white">Job Postings</h2>
          <p className="text-sm text-slate-400 mt-0.5">{jobs.length} postings · {jobs.filter((j) => j.is_active).length} active</p>
        </div>
        <button onClick={handleNew} className="bg-orange-600 hover:bg-orange-500 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors">
          + Post a Job
        </button>
      </div>

      {/* Add/Edit Form */}
      {showForm && (
        <div className="bg-slate-800/60 border border-slate-700/50 rounded-2xl p-6">
          <h3 className="text-base font-semibold text-white mb-5">{editingId ? 'Edit Job Posting' : 'New Job Posting'}</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1.5">Job Title *</label>
                <input type="text" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} required maxLength={200} placeholder="e.g. Linux System Administrator" className={inp} />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1.5">Company *</label>
                <input type="text" value={form.company} onChange={(e) => setForm((f) => ({ ...f, company: e.target.value }))} required maxLength={200} placeholder="e.g. TechCorp India Pvt Ltd" className={inp} />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1.5">Description *</label>
              <textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} required rows={5} maxLength={5000} placeholder="Job description, responsibilities, requirements…" className={inp + ' resize-none'} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1.5">Location Type *</label>
                <select value={form.location_type} onChange={(e) => setForm((f) => ({ ...f, location_type: e.target.value }))} required className={inp}>
                  {LOCATION_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1.5">City (optional)</label>
                <input type="text" value={form.city} onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))} maxLength={100} placeholder="e.g. Jaipur" className={inp} />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1.5">Salary Range (optional)</label>
                <input type="text" value={form.salary_range} onChange={(e) => setForm((f) => ({ ...f, salary_range: e.target.value }))} maxLength={100} placeholder="e.g. ₹8-12 LPA" className={inp} />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1.5">
                Required Certificate <span className="text-slate-600">(optional — only notify users with this cert)</span>
              </label>
              <input type="text" value={form.required_certificate} onChange={(e) => setForm((f) => ({ ...f, required_certificate: e.target.value.toUpperCase() }))} maxLength={50} placeholder="e.g. RHCSA, RHCE, CKA" className={inp} />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1.5">
                Apply URL <span className="text-slate-600">(optional — link to company's application page)</span>
              </label>
              <input type="url" value={form.apply_url} onChange={(e) => setForm((f) => ({ ...f, apply_url: e.target.value }))} maxLength={500} placeholder="https://company.com/apply or Google Form link" className={inp} />
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <button type="button" onClick={handleCancel} className="text-slate-400 hover:text-white text-sm font-medium px-4 py-2 rounded-lg border border-slate-600 transition-colors">Cancel</button>
              <button type="submit" disabled={saving} className="bg-orange-600 hover:bg-orange-500 disabled:bg-slate-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors">
                {saving ? 'Saving…' : editingId ? 'Update Job' : 'Create Job'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Jobs list */}
      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" /></div>
      ) : jobs.length === 0 ? (
        <div className="text-center py-12 text-slate-500 text-sm">No job postings yet. Click "Post a Job" to get started.</div>
      ) : (
        <div className="space-y-4">
          {jobs.map((job) => (
            <div key={job.id} className={`bg-slate-800/40 border rounded-xl p-5 transition-colors ${job.is_active ? 'border-slate-700/50' : 'border-slate-800 opacity-60'}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-base font-semibold text-white">{job.title}</h3>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${job.is_active ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-700 text-slate-500'}`}>
                      {job.is_active ? 'Active' : 'Inactive'}
                    </span>
                    {job.required_certificate && (
                      <span className="text-xs bg-orange-500/20 text-orange-400 px-2 py-0.5 rounded-full">🏆 {job.required_certificate}</span>
                    )}
                  </div>
                  <p className="text-sm text-orange-400 font-medium mt-0.5">{job.company}</p>
                  <div className="flex items-center gap-3 mt-1 text-xs text-slate-500 flex-wrap">
                    <span>📍 {job.city ? `${job.city} (${job.location_type})` : job.location_type}</span>
                    {job.salary_range && <span>💰 {job.salary_range}</span>}
                    {job.apply_url && <span className="text-green-500">🔗 Apply URL set</span>}
                    {job.email_sent_count > 0 && <span>📧 {job.email_sent_count} emails sent</span>}
                    {job.created_at && <span>🗓 {new Date(job.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>}
                  </div>
                  <p className="text-xs text-slate-500 mt-2 line-clamp-2">{job.description}</p>
                </div>
                <div className="flex flex-col gap-2 flex-shrink-0">
                  <button onClick={() => handleBlast(job)} disabled={blastingId === job.id || !job.is_active}
                    className="text-xs bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                    {blastingId === job.id ? '⏳ Sending…' : '📧 Blast'}
                  </button>
                  <button onClick={() => handleEdit(job)} className="text-xs bg-slate-700/50 hover:bg-slate-700 text-slate-300 px-3 py-1.5 rounded-lg transition-colors">
                    ✏️ Edit
                  </button>
                  <button onClick={() => handleToggle(job)} className={`text-xs px-3 py-1.5 rounded-lg transition-colors ${job.is_active ? 'bg-slate-700/50 hover:bg-slate-700 text-slate-400' : 'bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400'}`}>
                    {job.is_active ? '⏸ Deactivate' : '▶ Activate'}
                  </button>
                  <button onClick={() => handleDelete(job)} className="text-xs bg-red-500/10 hover:bg-red-500/20 text-red-400 px-3 py-1.5 rounded-lg transition-colors">
                    🗑 Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
